import { useState, useEffect } from "react"
import { FiUsers, FiActivity, FiSettings, FiCreditCard, FiStar, FiCheckCircle, FiMap, FiCalendar } from "react-icons/fi"
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from "recharts"

// Custom tooltip component for charts
const CustomTooltip = (props) => {
  const { active, payload, label, timeframe } = props
  if (!active || !payload || !payload.length) return null

  const count = payload[0].value

  // Format date based on timeframe
  const now = new Date()
  const day = now.getDate()
  const month = now.getMonth() + 1
  const year = now.getFullYear()
  const formattedToday = `${day < 10 ? "0" + day : day}-${month < 10 ? "0" + month : month}-${year}`

  // Different tooltip formats based on timeframe
  if (timeframe === "today") {
    // For today: Time, Date, New Users on separate lines
    const hour = Number.parseInt(label.split(":")[0])
    const ampm = hour >= 12 ? "PM" : "AM"
    const hour12 = hour % 12 || 12

    return (
      <div className="bg-[#333] p-2 rounded border border-gray-600 text-white">
        <p className="font-bold">{`${hour12}:00 ${ampm}`}</p>
        <p>{formattedToday}</p>
        <p>New Users: {count}</p>
      </div>
    )
  } else if (timeframe === "thisWeek") {
    // For this week: Day name, Date, New Users on separate lines
    return (
      <div className="bg-[#333] p-2 rounded border border-gray-600 text-white">
        <p className="font-bold">{label}</p>
        <p>{formattedToday}</p>
        <p>New Users: {count}</p>
      </div>
    )
  } else if (timeframe === "thisMonth") {
    // For this month: Date, New Users
    const day = label.split(" ")[1]
    const paddedDay = day < 10 ? `0${day}` : day
    const formattedDate = `${paddedDay}-${month < 10 ? "0" + month : month}-${year}`

    return (
      <div className="bg-[#333] p-2 rounded border border-gray-600 text-white">
        <p className="font-bold">{formattedDate}</p>
        <p>New Users: {count}</p>
      </div>
    )
  } else if (timeframe === "thisYear") {
    // For this year: Month Year, New Users
    return (
      <div className="bg-[#333] p-2 rounded border border-gray-600 text-white">
        <p className="font-bold">{`${label} ${year}`}</p>
        <p>New Users: {count}</p>
      </div>
    )
  } else {
    // Default format for other timeframes
    return (
      <div className="bg-[#333] p-2 rounded border border-gray-600 text-white">
        <p className="font-bold">{label}</p>
        <p>New Users: {count}</p>
      </div>
    )
  }
}

// Custom tooltip for country chart
const CountryTooltip = (props) => {
  const { active, payload, label } = props
  if (!active || !payload || !payload.length) return null

  return (
    <div className="bg-[#333] p-2 rounded border border-gray-600 text-white">
      <p className="font-bold">{label}</p>
      <p>Users: {payload[0].value}</p>
    </div>
  )
}

const StatisticsDashboard = () => {
  const [statistics, setStatistics] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showAllCountries, setShowAllCountries] = useState(false)
  const [selectedTimeframe, setSelectedTimeframe] = useState("today")

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        const SERVER_URL = process.env.REACT_APP_SERVER_URL
        const response = await fetch(`${SERVER_URL}/api/statistics`)
        if (!response.ok) {
          throw new Error("Failed to fetch statistics")
        }
        const data = await response.json()

        // Log the data to debug
        console.log("Statistics data:", data.statistics)

        // Check if country data exists
        if (data.statistics && data.statistics.geoStats && data.statistics.geoStats.countries) {
          console.log("Country data:", data.statistics.geoStats.countries)
        } else {
          console.warn("Country data is missing or empty")
        }

        setStatistics(data.statistics)
      } catch (err) {
        console.error("Error fetching statistics:", err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchStatistics()
    const interval = setInterval(fetchStatistics, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  if (loading) {
    return <div className="text-white">Loading...</div>
  }

  if (error) {
    return <div className="text-white">Error: {error}</div>
  }

  const formatNumber = (num) => new Intl.NumberFormat("en-US").format(num)

  // Safely handle country data
  const processCountryData = () => {
    if (!statistics || !statistics.geoStats || !statistics.geoStats.countries) {
      console.warn("No country data available")
      return []
    }

    // Convert country data to array and handle null values
    return Object.entries(statistics.geoStats.countries)
      .map(([country, data]) => ({
        country: country === "null" || !country ? "Unknown" : country,
        ...data,
      }))
      .sort((a, b) => b.count - a.count)
  }

  // Get sorted countries
  const sortedCountries = processCountryData()

  // Get top 10 countries for chart
  const topCountries = sortedCountries.slice(0, 10)

  // Prepare data for country chart
  const countryChartData = topCountries.map((item) => ({
    name: item.country,
    users: item.count,
  }))

  // Prepare data for registration chart based on selected timeframe
  const getRegistrationData = () => {
    if (!statistics || !statistics.registrationStats) return []

    let data = []

    switch (selectedTimeframe) {
      case "today":
        // Keep 24-hour format for x-axis labels
        data = statistics.registrationStats.today.data
        break
      case "thisWeek":
        data = statistics.registrationStats.thisWeek.data
        break
      case "thisMonth":
        data = statistics.registrationStats.thisMonth.data
        break
      case "thisYear":
        data = statistics.registrationStats.thisYear.data
        break
      case "lastFiveYears":
        data = statistics.registrationStats.lastFiveYears.data
        break
      default:
        data = []
    }

    return data
  }

  // Get total for selected timeframe
  const getTimeframeTotal = () => {
    if (!statistics || !statistics.registrationStats) return 0

    switch (selectedTimeframe) {
      case "today":
        return statistics.registrationStats.today.total
      case "thisWeek":
        return statistics.registrationStats.thisWeek.total
      case "thisMonth":
        return statistics.registrationStats.thisMonth.total
      case "thisYear":
        return statistics.registrationStats.thisYear.total
      case "lastFiveYears":
        return statistics.registrationStats.lastFiveYears.total
      default:
        return 0
    }
  }

  const registrationChartData = getRegistrationData()
  const timeframeTotal = getTimeframeTotal()

  const COLORS = [
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884d8",
    "#82ca9d",
    "#ffc658",
    "#8dd1e1",
    "#a4de6c",
    "#d0ed57",
  ]

  return (
    <div className="w-full h-full bg-black text-white p-6 pb-[94px] overflow-y-auto max-h-screen">
      {/* Header with Buff Statistics - no longer sticky */}
      <div className="flex items-center gap-3 mb-4 py-2">
        <img src="/loader.svg" alt="not" className="w-[18px] sm:w-[24px]" />
        <h1 className="text-2xl font-bold">Buff Statistics</h1>
      </div>

      <div className="flex flex-wrap gap-4 mb-6">
        {/* Total Users and Balance */}
        <div className="bg-[#292929] rounded-lg p-4 flex-1 min-w-[250px]">
          <div className="text-lg items-center">
            <p>
              Total Users: <span className="font-bold text-white">{formatNumber(statistics.totalUsers)}</span>
            </p>
            <p>
              Total Balance: <span className="font-bold text-white">{formatNumber(statistics.totalBalance)}</span>
            </p>
          </div>
        </div>

        {/* Online Users - Now First */}
        <div className="bg-[#292929] rounded-lg p-4 flex-1 min-w-[250px]">
          <div className="flex items-center space-x-2">
            <FiUsers className="text-yellow-400 text-2xl" />
            <div>
              <p className="text-sm">Online Users:</p>
              <p className="text-lg text-white font-bold">{formatNumber(statistics.currentlyOnline)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Active Users Stats - New Row with all active user metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Daily Active Users */}
        <div className="bg-[#292929] rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <FiActivity className="text-yellow-400 text-2xl" />
            <div>
              <p className="text-sm">Today Active Users:</p>
              <p className="text-lg text-white font-bold">{formatNumber(statistics.todayActiveUsers)}</p>
            </div>
          </div>
        </div>

        {/* This Week Active Users */}
        <div className="bg-[#292929] rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <FiActivity className="text-yellow-400 text-2xl" />
            <div>
              <p className="text-sm">This Week Active Users:</p>
              <p className="text-lg text-white font-bold">{formatNumber(statistics.thisWeekActiveUsers)}</p>
            </div>
          </div>
        </div>

        {/* This Month Active Users */}
        <div className="bg-[#292929] rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <FiActivity className="text-yellow-400 text-2xl" />
            <div>
              <p className="text-sm">This Month Active Users:</p>
              <p className="text-lg text-white font-bold">{formatNumber(statistics.thisMonthActiveUsers)}</p>
            </div>
          </div>
        </div>

        {/* This Year Active Users */}
        <div className="bg-[#292929] rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <FiActivity className="text-yellow-400 text-2xl" />
            <div>
              <p className="text-sm">This Year Active Users:</p>
              <p className="text-lg text-white font-bold">{formatNumber(statistics.thisYearActiveUsers)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Registration Stats */}
      <div className="bg-[#292929] rounded-lg p-4 mb-6">
        <div className="flex flex-wrap items-center gap-3 mb-3">
          <FiCalendar className="text-yellow-400 text-2xl" />
          <span className="text-lg text-yellow-400 font-bold">User Registrations:</span>

          <div className="ml-auto flex flex-wrap gap-2">
            <button
              onClick={() => setSelectedTimeframe("today")}
              className={`text-sm px-3 py-1 rounded-md ${selectedTimeframe === "today" ? "bg-yellow-400 text-black" : "bg-[#1e1e1e] text-white"}`}
            >
              Today
            </button>
            <button
              onClick={() => setSelectedTimeframe("thisWeek")}
              className={`text-sm px-3 py-1 rounded-md ${selectedTimeframe === "thisWeek" ? "bg-yellow-400 text-black" : "bg-[#1e1e1e] text-white"}`}
            >
              This Week
            </button>
            <button
              onClick={() => setSelectedTimeframe("thisMonth")}
              className={`text-sm px-3 py-1 rounded-md ${selectedTimeframe === "thisMonth" ? "bg-yellow-400 text-black" : "bg-[#1e1e1e] text-white"}`}
            >
              This Month
            </button>
            <button
              onClick={() => setSelectedTimeframe("thisYear")}
              className={`text-sm px-3 py-1 rounded-md ${selectedTimeframe === "thisYear" ? "bg-yellow-400 text-black" : "bg-[#1e1e1e] text-white"}`}
            >
              This Year
            </button>
            <button
              onClick={() => setSelectedTimeframe("lastFiveYears")}
              className={`text-sm px-3 py-1 rounded-md ${selectedTimeframe === "lastFiveYears" ? "bg-yellow-400 text-black" : "bg-[#1e1e1e] text-white"}`}
            >
              Last 5 Years
            </button>
          </div>
        </div>

        <div className="text-center mb-2">
          <span className="text-lg font-bold">Total: {formatNumber(timeframeTotal)} new users</span>
        </div>

        {/* Registration chart */}
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            {selectedTimeframe === "today" || selectedTimeframe === "thisWeek" ? (
              <BarChart data={registrationChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#555" />
                <XAxis dataKey="label" stroke="#ccc" />
                <YAxis stroke="#ccc" />
                <Tooltip content={<CustomTooltip timeframe={selectedTimeframe} />} />
                <Bar dataKey="count" name="New Users" fill="#00C49F" />
              </BarChart>
            ) : (
              <LineChart data={registrationChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#555" />
                <XAxis dataKey="label" stroke="#ccc" />
                <YAxis stroke="#ccc" />
                <Tooltip content={<CustomTooltip timeframe={selectedTimeframe} />} />
                <Line
                  type="monotone"
                  dataKey="count"
                  name="New Users"
                  stroke="#00C49F"
                  strokeWidth={2}
                  activeDot={{ r: 8 }}
                />
              </LineChart>
            )}
          </ResponsiveContainer>
        </div>
      </div>

      {/* Geolocation Stats */}
      <div className="bg-[#292929] rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <FiMap className="text-yellow-400 text-2xl" />
          <span className="text-lg text-yellow-400 font-bold">Geolocation: </span>
          <span className="text-white">{sortedCountries.length} Countries</span>
          <button
            onClick={() => setShowAllCountries(!showAllCountries)}
            className="ml-auto text-sm bg-yellow-400 text-black px-3 py-1 rounded-md hover:bg-yellow-500"
          >
            {showAllCountries ? "Show Top 10" : "Show All"}
          </button>
        </div>

        {sortedCountries.length === 0 ? (
          <div className="text-center py-4">
            <p>No country data available. Please ensure country detection is enabled.</p>
          </div>
        ) : (
          <>
            {/* Chart for top countries */}
            {!showAllCountries && (
              <div className="h-64 w-full mb-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={countryChartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#555" />
                    <XAxis dataKey="name" stroke="#ccc" />
                    <YAxis stroke="#ccc" />
                    <Tooltip content={<CountryTooltip />} />
                    <Bar dataKey="users" name="Users" fill="#F3C025">
                      {countryChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Country list as table when showing all */}
            {showAllCountries && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#1e1e1e]">
                      <th className="p-2 text-left">No.</th>
                      <th className="p-2 text-left">Country</th>
                      <th className="p-2 text-right">Total Users</th>
                      <th className="p-2 text-right">Percentage</th>
                      <th className="p-2 text-right">Active Today</th>
                      <th className="p-2 text-right">New Today</th>
                      <th className="p-2 text-right">New This Week</th>
                      <th className="p-2 text-right">New This Month</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCountries.map((country, index) => (
                      <tr key={country.country} className={index % 2 === 0 ? "bg-[#292929]" : "bg-[#252525]"}>
                        <td className="p-2">{index + 1}</td>
                        <td className="p-2">{country.country}</td>
                        <td className="p-2 text-right">{formatNumber(country.count)}</td>
                        <td className="p-2 text-right text-yellow-400">{country.percent}%</td>
                        <td className="p-2 text-right">{formatNumber(country.activeToday || 0)}</td>
                        <td className="p-2 text-right">{formatNumber(country.newToday || 0)}</td>
                        <td className="p-2 text-right">{formatNumber(country.newThisWeek || 0)}</td>
                        <td className="p-2 text-right">{formatNumber(country.newThisMonth || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* System Stats */}
      <div className="bg-[#292929] rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <FiSettings className="text-yellow-400 text-2xl" />
          <span className="text-lg text-yellow-400 font-bold">System:</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="font-bold text-gray-300">iOS:</div>
            <div>Users Count: {formatNumber(statistics.system.iOS.count)}</div>
            <div className="text-yellow-400">Users Percent: {statistics.system.iOS.percent}%</div>
          </div>

          <div>
            <div className="font-bold text-gray-300">Android:</div>
            <div>Users Count: {formatNumber(statistics.system.Android.count)}</div>
            <div className="text-yellow-400">Users Percent: {statistics.system.Android.percent}%</div>
          </div>

          <div>
            <div className="font-bold text-gray-300">Other:</div>
            <div>Users Count: {formatNumber(statistics.system.Other.count)}</div>
            <div className="text-yellow-400">Users Percent: {statistics.system.Other.percent}%</div>
          </div>
        </div>
      </div>

      {/* Wallets Stats */}
      <div className="bg-[#292929] rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <FiCreditCard className="text-yellow-400 text-2xl" />
          <span className="text-lg text-yellow-400 font-bold">Wallets:</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="font-bold text-gray-300">With Wallet:</div>
            <div>Users Count: {formatNumber(statistics.wallets.withWallet.count)}</div>
            <div className="text-yellow-400">Users Percent: {statistics.wallets.withWallet.percent}%</div>
          </div>

          <div>
            <div className="font-bold text-gray-300">Without Wallet:</div>
            <div>Users Count: {formatNumber(statistics.wallets.withoutWallet.count)}</div>
            <div className="text-yellow-400">Users Percent: {statistics.wallets.withoutWallet.percent}%</div>
          </div>
        </div>
      </div>

      {/* Premium Stats */}
      <div className="bg-[#292929] rounded-lg p-4 mb-6">
        <div className="flex items-center gap-3 mb-3">
          <FiStar className="text-yellow-400 text-2xl" />
          <span className="text-lg text-yellow-400 font-bold">Premium:</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="font-bold text-gray-300">TG Premium:</div>
            <div>Users Count: {formatNumber(statistics.premiumUsers.tgPremium.count)}</div>
            <div className="text-yellow-400">Users Percent: {statistics.premiumUsers.tgPremium.percent}%</div>
          </div>

          <div>
            <div className="font-bold text-gray-300">Without TG Premium:</div>
            <div>Users Count: {formatNumber(statistics.premiumUsers.withoutTG.count)}</div>
            <div className="text-yellow-400">Users Percent: {statistics.premiumUsers.withoutTG.percent}%</div>
          </div>
        </div>
      </div>

      {/* tonTasks Stats (if needed) */}
      {statistics.tonTasks && (
        <div className="bg-[#292929] rounded-lg p-4 mt-6">
          <div className="flex items-center gap-3 mb-3">
            <FiCheckCircle className="text-yellow-400 text-2xl" />
            <span className="text-lg text-yellow-400 font-bold">tonTasks:</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="font-bold text-gray-300">tonTasks Completed:</div>
              <div>Users Count: {formatNumber(statistics.tonTasks.complete.count)}</div>
              <div className="text-yellow-400">Users Percent: {statistics.tonTasks.complete.percent}%</div>
            </div>

            <div>
              <div className="font-bold text-gray-300">Without tonTasks:</div>
              <div>Users Count: {formatNumber(statistics.tonTasks.without.count)}</div>
              <div className="text-yellow-400">Users Percent: {statistics.tonTasks.without.percent}%</div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StatisticsDashboard

